import { useState } from 'react'
import { Check, Copy, KeyRound } from 'lucide-react'

export interface KeysShownOnceProps {
  title?: string
  /** label → secret */
  secrets: Record<string, string>
  note?: string
}

/** A secret shown exactly once (U3): copy buttons, a warning, never shown again after the drawer closes. */
export function KeysShownOnce({
  title = 'Shown once',
  secrets,
  note = 'Copy it now. For your safety the portal keeps only the last 4 characters; you cannot see it again, only replace it.',
}: KeysShownOnceProps) {
  const [copied, setCopied] = useState<string | null>(null)
  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(label)
      setTimeout(() => setCopied(null), 1500)
    } catch {
      setCopied(null)
    }
  }
  return (
    <div className="rounded-md border border-accent/60 bg-accent-soft p-3" role="region" aria-label={title}>
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-accent-text">
        <KeyRound size={14} /> {title}
      </div>
      <div className="space-y-2">
        {Object.entries(secrets).map(([label, value]) => (
          <div key={label}>
            <p className="text-2xs font-medium text-accent-text">{label}</p>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 select-all truncate rounded border border-slate-200 bg-white px-2 py-1 font-mono text-2xs text-default dark:border-slate-700 dark:bg-slate-900">
                {value}
              </code>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => copy(label, value)}
                aria-label={`Copy ${label}`}
              >
                {copied === label ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />}
                {copied === label ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-2 text-2xs text-accent-text">{note}</p>
    </div>
  )
}
